"use client";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback, useMemo, useRef } from "react";

export type UseQueryParamsReturnType = {
  push: ({
    path,
    query,
  }: {
    path?: string;
    query: Record<string, any>;
  }) => void;
  getAll: () => Record<string, any>;
  reset: () => void;
  get: (key: string) => any;
  _delete: (keys: string | string[]) => void;
  deleteAllQueries: (params: string[]) => void;
  searchParams: string;
};

/**
 * `router.push()` is async in the App Router: `useSearchParams()` keeps
 * reporting the previous query string until the navigation commits. Every
 * writer below rebuilds the WHOLE query string, so a writer that starts from
 * that snapshot silently reverts any earlier write still in flight.
 *
 * That is how closing an interviews modal lost the status filter (AWOPS-3296):
 * the table keeps its filters in a single RISON `table=(...)` param, and the
 * close handler's `deleteAllQueries` — which never lists `table` — rebuilt the
 * query string from a snapshot taken before the filter write landed, rolling
 * `table` back to its pre-filter value. `useTableFetch` then mirrored that URL
 * into Redux as `setFilters({})`, resetting the status tab to "All".
 *
 * `pendingWrite` records the query string the last writer produced, keyed by the
 * committed value it was derived from, so a following writer builds on it
 * instead of on the stale snapshot. It is discarded as soon as the committed
 * value moves — whether that is our own write landing or an unrelated
 * navigation (back/forward, a link) — so it can never pin a stale value.
 */
let pendingWrite: { pathname: string; from: string; to: string } | null = null;

/** Latest query string intended for `pathname`, including writes still in flight. */
function liveSearch(pathname: string, committed: string): string {
  // Module state is per-process on the server and shared across requests, so it
  // must never influence SSR output. Writers only ever run from browser event
  // handlers, so there is nothing to track during a server render anyway.
  if (typeof window === "undefined") return committed;
  if (!pendingWrite) return committed;
  if (pendingWrite.pathname !== pathname || pendingWrite.from !== committed) {
    pendingWrite = null;
    return committed;
  }
  return pendingWrite.to;
}

function recordWrite(pathname: string, committed: string, next: string) {
  pendingWrite = { pathname, from: committed, to: next };
}

function useQueryParams(): UseQueryParamsReturnType {
  const searchParams = useSearchParams() || {};
  const router = useRouter();
  const pathname = usePathname() || "";

  const searchParamsString = useMemo(
    () => searchParams?.toString() || "",
    [searchParams],
  );

  // Read through a ref so the writers below can see the newest committed value
  // without taking it as a dependency. Keeping `push` referentially stable
  // matters: `useTableQuery` wraps it, and `useTableFetch` lists those wrappers
  // in effect dependency arrays that push to the URL — making them change on
  // every navigation would turn those effects into a push loop.
  const committedRef = useRef(searchParamsString);
  committedRef.current = searchParamsString;

  const push = useCallback(
    ({ path, query }: { path?: string; query: Record<string, any> }) => {
      const params = new URLSearchParams();
      for (const key in query) {
        params.set(key, String(query[key]));
      }
      const next = params.toString();
      const url = (path || pathname) + "?" + next;
      // A push to another route starts that route's query string from scratch,
      // so there is nothing on this pathname for a later writer to build on.
      if (!path || path === pathname) {
        recordWrite(pathname, committedRef.current, next);
      }
      router.push(url, { scroll: false });
    },
    [pathname, router],
  );

  const get = useCallback(
    (key: string) => {
      const current = new URLSearchParams(
        liveSearch(pathname, committedRef.current),
      );
      const value = current.get(key);
      return value || undefined;
    },
    [pathname, searchParamsString],
  );

  const getAll = useCallback(() => {
    const current = new URLSearchParams(
      liveSearch(pathname, committedRef.current),
    );
    const query: Record<string, any> = {};
    current.forEach((value, key) => {
      query[key] = value;
    });
    return query;
  }, [pathname, searchParamsString]);

  const deleteKeys = useCallback(
    (keys: string[]) => {
      const current = new URLSearchParams(
        liveSearch(pathname, committedRef.current),
      );
      keys.forEach((key) => current.delete(key));
      const next = current.toString();
      recordWrite(pathname, committedRef.current, next);
      router.push(pathname + "?" + next, {
        scroll: false,
        shallow: true,
      } as any);
    },
    [router, pathname],
  );

  const _delete = useCallback(
    (type: string | string[]) => deleteKeys(Array.isArray(type) ? type : [type]),
    [deleteKeys],
  );

  const deleteAllQueries = useCallback(
    (params: string[]) => deleteKeys(params),
    [deleteKeys],
  );

  const reset = useCallback(() => {
    recordWrite(pathname, committedRef.current, "");
    router.replace(pathname, { scroll: false, shallow: true } as any);
  }, [router, pathname]);

  return useMemo(
    () => ({
      push,
      getAll,
      reset,
      get,
      _delete,
      deleteAllQueries,
      searchParams: searchParamsString,
    }),
    [push, getAll, reset, get, _delete, deleteAllQueries, searchParamsString],
  );
}

export default useQueryParams;
